import {
  ALLERGEN_WRITE_PROVENANCES,
  FOOD_MEAL_TIMES,
  foodCreateProblems,
  stripResearchLinks,
  type AdminFoodCreateRequest,
  type AdminFoodResearchDraft,
  type AdminFoodResearchMetadata,
  type AllergenWriteProvenanceName,
  type CostBandName,
  type FoodCuisineName,
  type FoodDietClassName,
  type FoodKindName,
  type FoodMealTimeName,
  type MealRoleName,
  type PortionUnitName,
  type PrepTimeBandName,
  type RecipeConfidenceName,
  type RecipeLineMatchName,
} from '@steado/admin-contract/foods';
import type { AllergenName } from '@steado/admin-contract/user-detail';

/**
 * The shape the new-food form edits.
 *
 * Framework-free so the rules can be asserted without rendering, and so the
 * one genuinely fiddly part — turning a research draft into form values
 * without letting the model's answer tick anything an operator has to stand
 * behind — is a pure function with a test rather than a `useEffect`.
 *
 * Every number is held as text. An emptied numeric input is not a zero, and a
 * form that turned it into one would let someone save a food claiming 0 kcal
 * because they cleared the field to retype it.
 */
export interface FoodFormValues {
  name: string;
  kind: FoodKindName;
  category: string;

  kcal: string;
  protein: string;
  carbs: string;
  fat: string;
  fibre: string;

  dietClass: FoodDietClassName;
  hasOnionGarlic: boolean;
  /**
   * Whether everyday Jain practice avoids it. Null is "not stated", which the
   * server lands as excluded unless a complete recipe clears it.
   */
  jainExcluded: boolean | null;
  hasBeef: boolean;
  allergens: AllergenName[];
  /**
   * Starts at `unassessed` and is never advanced by a research draft. It is
   * the operator's claim that the list is complete, and `source_stated` is
   * what makes the plan shortlist trust it — see `ALLERGEN_WRITE_PROVENANCES`.
   */
  allergenSource: AllergenWriteProvenanceName;

  servableAsIs: boolean;
  isCommonInIndia: boolean;
  cuisines: FoodCuisineName[];
  mealRole: MealRoleName | null;
  prepTimeBand: PrepTimeBandName | null;
  costBand: CostBandName | null;
  mealTimes: FoodMealTimeName[];
  /** Month numbers, held as toggles over 1..12. */
  seasonMonths: number[];

  /** Null when the food rides its role's default rather than overriding it. */
  servingPolicy: ServingPolicyValues | null;
  aliases: AliasValues[];
  portions: PortionValues[];
  recipeLines: RecipeLineValues[];
  recipeConfidence: RecipeConfidenceName;
  recipeNote: string;

  /** True once anything here came from a draft. Recorded in the audit row only. */
  researched: boolean;
  /** Describes the research draft; the operator may subsequently edit its values. */
  research: AdminFoodResearchMetadata | null;
}

export interface ServingPolicyValues {
  min: string;
  typical: string;
  max: string;
  maxDay: string;
  elastic: boolean;
}

export interface AliasValues {
  alias: string;
  language: string;
}

export interface PortionValues {
  label: string;
  unit: PortionUnitName;
  grams: string;
  isDefault: boolean;
}

export interface RecipeLineValues {
  rawName: string;
  ingredientFoodId: string | null;
  /** The catalog row's identity name, so the line can show what it points at. */
  ingredientName: string | null;
  /**
   * How the draft found the row, from the server's matcher. Null when nothing
   * matched, and cleared the moment the operator picks by hand: the tier
   * describes the model's line, never a person's choice.
   */
  matchedBy: RecipeLineMatchName | null;
  grams: string;
  retentionGroup: string;
}

export const EMPTY_FOOD_FORM: FoodFormValues = {
  name: '',
  kind: 'dish',
  category: '',
  kcal: '',
  protein: '',
  carbs: '',
  fat: '',
  fibre: '',
  // Vegetarian rather than the least permissive class, unlike the food-scan
  // decomposition's NON_VEGETARIAN default. That default protects against a
  // model's unreviewed guess reaching a diet filter; here a person picks the
  // value and looks at it, and starting at non-vegetarian would make the
  // common case the one most likely to be left wrong by a tab past it.
  dietClass: 'vegetarian',
  hasOnionGarlic: false,
  jainExcluded: null,
  hasBeef: false,
  allergens: [],
  allergenSource: 'unassessed',
  servableAsIs: true,
  isCommonInIndia: true,
  cuisines: [],
  mealRole: null,
  prepTimeBand: null,
  costBand: null,
  mealTimes: [],
  seasonMonths: [],
  servingPolicy: null,
  aliases: [],
  portions: [],
  recipeLines: [],
  recipeConfidence: 'medium',
  recipeNote: '',
  researched: false,
  research: null,
};

/**
 * A research draft as form values.
 *
 * Three things a draft deliberately cannot set, and each is the point:
 *
 * - **`allergenSource`.** Replacing the list with model suggestions clears
 *   any earlier confirmation. The operator must check the new list before
 *   it can be treated as source-stated.
 * - **`hasBeef`.** It is a hard exclusion from search and the shortlist both.
 *   A draft may only ever set it *true* — a model saying "no beef" is not
 *   evidence, and letting it clear a flag an operator ticked is the wrong
 *   direction to be wrong in.
 * - **`name` and `kind`.** The operator typed those; a draft answers about
 *   them rather than replacing them.
 */
export function fromResearchDraft(current: FoodFormValues, draft: AdminFoodResearchDraft): FoodFormValues {
  const macros = draft.research?.nutritionBasis === 'unresolved' ? null : draft.macrosPer100g;
  const portions = draft.research?.portionBasis === 'unresolved' ? [] : draft.portions;
  const policy = draft.research?.portionBasis === 'unresolved' ? null : draft.servingPolicy;

  return {
    ...current,
    category: draft.category === null ? current.category : stripResearchLinks(draft.category),
    kcal: macros === null ? '' : String(macros.kcal),
    protein: macros === null ? '' : String(macros.proteinG),
    carbs: macros === null ? '' : String(macros.carbsG),
    fat: macros === null ? '' : String(macros.fatG),
    fibre: String(macros?.fibreG ?? ''),
    dietClass: draft.dietClass ?? current.dietClass,
    hasOnionGarlic: draft.hasOnionGarlic ?? current.hasOnionGarlic,
    jainExcluded: draft.jainExcluded ?? current.jainExcluded,
    hasBeef: current.hasBeef || draft.hasBeef === true,
    allergens: [...draft.allergens],
    allergenSource: 'unassessed',
    servableAsIs: draft.servableAsIs ?? current.servableAsIs,
    isCommonInIndia: draft.isCommonInIndia ?? current.isCommonInIndia,
    cuisines: [...draft.cuisines],
    mealRole: draft.mealRole,
    prepTimeBand: draft.prepTimeBand,
    costBand: draft.costBand,
    mealTimes: [...draft.mealTimes],
    seasonMonths: [...draft.seasonMonths],
    servingPolicy:
      policy === null
        ? null
        : {
            min: String(policy.minPortions),
            typical: String(policy.typicalPortions),
            max: String(policy.maxPortions),
            maxDay: policy.maxDayPortions === null ? '' : String(policy.maxDayPortions),
            elastic: policy.elastic,
          },
    aliases: draft.aliases.map((alias) => ({
      alias: stripResearchLinks(alias.alias),
      language: stripResearchLinks(alias.language ?? ''),
    })),
    portions: portions.map((portion) => ({
      label: stripResearchLinks(portion.label),
      unit: portion.unit,
      grams: String(portion.grams),
      isDefault: portion.isDefault,
    })),
    recipeLines: draft.recipeLines.map((line) => ({
      rawName: stripResearchLinks(line.rawName),
      ingredientFoodId: line.ingredientFoodId,
      ingredientName: line.ingredientName === null ? null : stripResearchLinks(line.ingredientName),
      matchedBy: line.matchedBy,
      grams: String(line.grams),
      retentionGroup: stripResearchLinks(line.retentionGroup ?? ''),
    })),
    recipeConfidence: draft.confidence ?? current.recipeConfidence,
    recipeNote: stripResearchLinks(draft.note ?? ''),
    researched: true,
    research: draft.research === undefined ? null : cleanResearchMetadata(draft.research),
  };
}

/**
 * Why the form cannot be saved yet, in the operator's words.
 *
 * These mirror the contract's own minimums rather than adding a second set of
 * rules, so the button is shut for the same reasons the server would refuse —
 * and the cross-field rules come from `foodCreateProblems`, which is the
 * refinement the server actually runs.
 */
export function formIssues(values: FoodFormValues): string[] {
  const issues: string[] = [];

  if (values.name.trim().length < 2) issues.push('Name it.');

  for (const [label, raw] of [
    ['Calories', values.kcal],
    ['Protein', values.protein],
    ['Carbs', values.carbs],
    ['Fat', values.fat],
  ] as const) {
    if (parseNumber(raw) === null) issues.push(`${label} per 100 g is missing or not a number.`);
  }
  if (values.fibre.trim() !== '' && parseNumber(values.fibre) === null) {
    issues.push('Fibre is not a number. Leave it blank if the figure is unknown.');
  }

  for (const portion of values.portions) {
    if (portion.label.trim() === '') issues.push('A portion has no label.');
    else if (/^[0-9]/.test(portion.label.trim())) {
      issues.push(`"${portion.label.trim()}" starts with a number — a label is a bare noun.`);
    }
    if (parseNumber(portion.grams) === null) {
      issues.push(`Portion "${portion.label.trim() || '(unnamed)'}" has no weight in grams.`);
    }
  }

  for (const alias of values.aliases) {
    if (alias.alias.trim() === '') issues.push('An alias is empty.');
  }

  if (values.servingPolicy !== null) {
    const policy = values.servingPolicy;
    const numbers = [policy.min, policy.typical, policy.max].map(parseNumber);
    if (numbers.some((value) => value === null || value <= 0)) {
      issues.push('The serving policy needs a min, a typical and a max, all above zero.');
    } else {
      const [min, typical, max] = numbers as [number, number, number];
      if (!(min <= typical && typical <= max)) {
        issues.push('The serving policy must read min ≤ typical ≤ max.');
      }
      const maxDay = policy.maxDay.trim() === '' ? null : parseNumber(policy.maxDay);
      if (policy.maxDay.trim() !== '' && (maxDay === null || maxDay < max)) {
        issues.push('A daily cap must be at least the per-meal maximum.');
      }
    }
  }

  for (const line of values.recipeLines) {
    if (line.rawName.trim() === '') issues.push('A recipe line has no ingredient name.');
    if (parseNumber(line.grams) === null) {
      issues.push(`Recipe line "${line.rawName.trim() || '(unnamed)'}" has no weight in grams.`);
    }
  }

  // The server's own cross-field refinement, run against the same shape, so
  // the two cannot disagree about what a complete food is.
  for (const problem of foodCreateProblems({
    kind: values.kind,
    portions: values.portions.map((portion) => ({ label: portion.label, isDefault: portion.isDefault })),
    mealTimes: values.mealTimes,
    seasonMonths: values.seasonMonths,
    recipe: values.recipeLines.length === 0 ? null : {},
  })) {
    issues.push(problem.message);
  }

  return issues;
}

/** Only call this when `formIssues` is empty; it assumes the numbers parse. */
export function toCreateRequest(values: FoodFormValues): AdminFoodCreateRequest {
  const category = values.category.trim();
  const fibre = values.fibre.trim();
  const recipeNote = values.researched ? stripResearchLinks(values.recipeNote) : values.recipeNote.trim();

  return {
    name: values.name.trim(),
    kind: values.kind,
    category: category === '' ? null : category,
    kcalPer100g: parseNumber(values.kcal) ?? 0,
    proteinGPer100g: parseNumber(values.protein) ?? 0,
    carbsGPer100g: parseNumber(values.carbs) ?? 0,
    fatGPer100g: parseNumber(values.fat) ?? 0,
    fibreGPer100g: fibre === '' ? null : parseNumber(fibre),
    dietClass: values.dietClass,
    hasOnionGarlic: values.hasOnionGarlic,
    ...(values.jainExcluded === null ? {} : { jainExcluded: values.jainExcluded }),
    hasBeef: values.hasBeef,
    // An unassessed row states no allergens: the empty list is what UNASSESSED
    // already means, and sending a list beside it would claim an assessment
    // the operator declined to make.
    allergens: values.allergenSource === 'unassessed' ? [] : values.allergens,
    allergenSource: values.allergenSource,
    servableAsIs: values.servableAsIs,
    isCommonInIndia: values.isCommonInIndia,
    cuisines: values.cuisines,
    mealRole: values.mealRole,
    prepTimeBand: values.prepTimeBand,
    costBand: values.costBand,
    mealTimes: values.mealTimes,
    seasonMonths: [...values.seasonMonths].sort((a, b) => a - b),
    servingPolicy:
      values.servingPolicy === null
        ? null
        : {
            minPortions: parseNumber(values.servingPolicy.min) ?? 0,
            typicalPortions: parseNumber(values.servingPolicy.typical) ?? 0,
            maxPortions: parseNumber(values.servingPolicy.max) ?? 0,
            maxDayPortions:
              values.servingPolicy.maxDay.trim() === '' ? null : parseNumber(values.servingPolicy.maxDay),
            elastic: values.servingPolicy.elastic,
          },
    aliases: values.aliases.map((alias) => ({
      alias: alias.alias.trim(),
      language: alias.language.trim() === '' ? null : alias.language.trim(),
    })),
    portions: values.portions.map((portion) => ({
      label: portion.label.trim(),
      unit: portion.unit,
      grams: parseNumber(portion.grams) ?? 0,
      isDefault: portion.isDefault,
    })),
    recipe:
      values.kind !== 'dish' || values.recipeLines.length === 0
        ? null
        : {
            lines: values.recipeLines.map((line) => ({
              rawName: line.rawName.trim(),
              ingredientFoodId: line.ingredientFoodId,
              grams: parseNumber(line.grams) ?? 0,
              retentionGroup: line.retentionGroup.trim() === '' ? null : line.retentionGroup.trim(),
            })),
            confidence: values.recipeConfidence,
            ...(recipeNote === '' ? {} : { note: recipeNote }),
          },
    researched: values.researched,
    ...(values.research === null ? {} : { research: cleanResearchMetadata(values.research) }),
  };
}

function cleanResearchMetadata(research: AdminFoodResearchMetadata): AdminFoodResearchMetadata {
  return {
    ...research,
    summary: stripResearchLinks(research.summary),
    assumptions: research.assumptions.map(stripResearchLinks).filter(Boolean),
  };
}

/**
 * A finite number, or null. `Number('')` is 0 and `Number(' ')` is 0, which is
 * exactly the confusion between "zero" and "blank" this form is built to avoid.
 */
export function parseNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

export const MONTH_LABELS: Readonly<Record<number, string>> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

/** Re-exported so the fields component does not import the contract twice. */
export { ALLERGEN_WRITE_PROVENANCES, FOOD_MEAL_TIMES };
